import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
  fetchConversionRatios,
  fetchRevenueGoalHqRollup,
  updateConversionRatio
} from 'api'
import { navigationPaths } from 'app/config'
import { showToast } from 'app/helpers'

import GuideButton from './GuideButton'
import { defaultPlanningMonth, inr, monthLabel, monthOptions } from './monthHelpers'
import { useFillHeight } from './useFillHeight'

// The HQ super-manager view (Shruti's seat): one row per centre with the gap
// plus the accountability signals her daily Slack posts carried — how many
// Recharge calls were logged today and by whom, and how many tickets are
// overdue. Worst gap first. Click a centre to jump into its chase list.
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2, 3) },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(1.5)
  },
  titleRow: { display: 'flex', alignItems: 'center', gap: 6 },
  ratioPanel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    padding: theme.spacing(1.25, 2),
    marginBottom: theme.spacing(1.5),
    borderLeft: '4px solid #6A1B9A'
  },
  ratioInput: { width: 92 },
  tableWrap: { maxHeight: '68vh' },
  muted: { color: theme.palette.text.secondary },
  money: { whiteSpace: 'nowrap' },
  gapChip: { backgroundColor: '#EF5D34', color: '#fff', fontWeight: 600 },
  zeroLogged: { color: '#C62828', fontWeight: 700 },
  clickable: { cursor: 'pointer' },
  spoc: { whiteSpace: 'nowrap' },
  empty: { padding: theme.spacing(6), textAlign: 'center', color: theme.palette.text.secondary }
}))

const HqRollup = () => {
  const classes = useStyles()
  const history = useHistory()
  const [month, setMonth] = useState(defaultPlanningMonth())
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [tableRef, tableMaxHeight] = useFillHeight()
  // Enquiry-pipeline conversion ratios (HQ knob). We edit the GLOBAL default
  // here — the % of enquiries that convert, by intent — as whole percents.
  const [ratioPct, setRatioPct] = useState({ high: '', medium: '', low: '', winback: '' })
  const [savingRatio, setSavingRatio] = useState(false)

  const load = useCallback(async (m) => {
    setLoading(true)
    try {
      const data = await fetchRevenueGoalHqRollup(m)
      setRows(data?.centres || [])
    } catch (error) {
      showToast('Could not load the HQ rollup', 'error')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(month)
  }, [month, load])

  useEffect(() => {
    const loadRatios = async () => {
      try {
        const data = await fetchConversionRatios()
        const global = (data?.ratios || []).find((r) => r.centre_uuid === null)
        if (global) {
          setRatioPct({
            high: Math.round(global.high_ratio * 100),
            medium: Math.round(global.medium_ratio * 100),
            low: Math.round(global.low_ratio * 100),
            winback: Math.round((global.winback_ratio ?? 0.1) * 100)
          })
        }
      } catch (error) {
        // Non-fatal — the rollup still loads without the ratio panel values.
      }
    }
    loadRatios()
  }, [])

  const saveRatios = async () => {
    setSavingRatio(true)
    try {
      await updateConversionRatio({
        centre_uuid: null,
        high_ratio: Number(ratioPct.high) / 100,
        medium_ratio: Number(ratioPct.medium) / 100,
        low_ratio: Number(ratioPct.low) / 100,
        winback_ratio: Number(ratioPct.winback) / 100
      })
      showToast('Conversion ratios saved — forecasts update on next reload')
    } catch (error) {
      showToast('Could not save the ratios', 'error')
    } finally {
      setSavingRatio(false)
    }
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.titleRow}>
          <Typography variant="h6">Revenue Goals — HQ Follow-up</Typography>
          <GuideButton title="How the HQ follow-up view works">
            <Typography variant="body2" gutterBottom>
              One row per centre, worst gap first. Beyond the gap, it shows
              whether each centre&apos;s team is actually working their list:
            </Typography>
            <ol style={{ paddingLeft: 18, lineHeight: 1.7 }}>
              <li>
                <b>Logged today</b> — Recharge calls the team entered into
                Alfred today, and who entered them. A red <b>0</b> means nobody
                has logged a recharge follow-up today.
              </li>
              <li>
                <b>Overdue tickets</b> — CRM tickets whose follow-up date has
                passed and are still open.
              </li>
            </ol>
            <Typography variant="body2">
              Click a centre to open its chase list. The who-logged breakdown is
              HQ-only — centre teams don&apos;t see each other.
            </Typography>
          </GuideButton>
        </Box>
        <FormControl variant="outlined" size="small">
          <InputLabel>Month</InputLabel>
          <Select label="Month" value={month} onChange={(e) => setMonth(e.target.value)}>
            {monthOptions(6).map((m) => (
              <MenuItem key={m} value={m}>
                {monthLabel(m)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper className={classes.ratioPanel}>
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            Enquiry conversion ratios
          </Typography>
          <GuideButton title="Conversion ratios">
            <Typography variant="body2" gutterBottom>
              The share of enquiries that convert to enrolment, by intent. The
              enquiry pipeline weights each parent&apos;s potential fee by these
              — so if High is 60%, a keen parent&apos;s full monthly fee counts
              at 60% in every centre&apos;s forecast.
            </Typography>
            <Typography variant="body2" gutterBottom>
              Winback % works the same way for lapsed parents in the chase
              list: their old fee × this % = the estimated comeback value.
              They&apos;re warm leads — well above cold-call rates, below a
              live enquiry.
            </Typography>
            <Typography variant="body2">
              This is the global default for all centres. Lower it to be more
              conservative; the forecast recomputes live on the next reload.
            </Typography>
          </GuideButton>
        </Box>
        {['high', 'medium', 'low', 'winback'].map((k) => (
          <TextField
            key={k}
            className={classes.ratioInput}
            size="small"
            variant="outlined"
            type="number"
            label={`${k[0].toUpperCase()}${k.slice(1)} %`}
            value={ratioPct[k]}
            onChange={(e) => {
              const v = e.target.value
              setRatioPct((p) => ({ ...p, [k]: v }))
            }}
            inputProps={{ min: 0, max: 100 }}
          />
        ))}
        <Tooltip title="Save these conversion % — every centre's enquiry forecast recalculates with them">
          <span>
            <Button
              size="small"
              variant="outlined"
              style={{ textTransform: 'none' }}
              disabled={savingRatio}
              onClick={saveRatios}
            >
              {savingRatio ? 'Saving…' : 'Save ratios'}
            </Button>
          </span>
        </Tooltip>
      </Paper>

      {loading ? (
        <Box className={classes.empty}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          ref={tableRef}
          style={{ maxHeight: tableMaxHeight }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Centre</TableCell>
                <TableCell align="right">
                  <Tooltip title="The month's revenue goal, set on Set Targets">
                    <span>Target</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Money already secured — advance fees + wallet + finance adjustment">
                    <span>Committed</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Target minus committed — what the centre still has to collect">
                    <span>Gap</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Recharge calls the centre team entered into Alfred today — red 0 means nobody has logged one yet">
                    <span>Logged today</span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Tooltip title="Which team member logged them (HQ-only view)">
                    <span>Who logged</span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Open CRM tickets whose follow-up date has already passed">
                    <span>Overdue tickets</span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={row.centre_uuid}
                  hover
                  className={classes.clickable}
                  onClick={() =>
                    history.push(
                      `${navigationPaths.revenueGoalsMyCentre}?centre=${row.centre_uuid}`
                    )
                  }
                >
                  <TableCell>{row.centre_name}</TableCell>
                  <TableCell align="right" className={classes.money}>
                    {row.target === null ? (
                      <span className={classes.muted}>—</span>
                    ) : (
                      inr(row.target)
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.money}>
                    {inr(row.committed)}
                  </TableCell>
                  <TableCell align="right">
                    {row.gap === null ? (
                      <span className={classes.muted}>—</span>
                    ) : row.gap > 0 ? (
                      <Chip size="small" className={classes.gapChip} label={inr(row.gap)} />
                    ) : (
                      <span className={classes.muted}>0</span>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <span className={row.logged_today === 0 ? classes.zeroLogged : ''}>
                      {row.logged_today}
                    </span>
                  </TableCell>
                  <TableCell>
                    {row.logged_today_by_spoc && row.logged_today_by_spoc.length ? (
                      <Tooltip
                        title={row.logged_today_by_spoc
                          .map((s) => `${s.spoc}: ${s.count}`)
                          .join(' · ')}
                      >
                        <span className={classes.spoc}>
                          {row.logged_today_by_spoc
                            .slice(0, 2)
                            .map((s) => `${s.spoc} (${s.count})`)
                            .join(', ')}
                          {row.logged_today_by_spoc.length > 2 ? ' …' : ''}
                        </span>
                      </Tooltip>
                    ) : (
                      <span className={classes.muted}>—</span>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {row.overdue_tickets > 0 ? (
                      <span style={{ color: '#C62828', fontWeight: 700 }}>
                        {row.overdue_tickets}
                      </span>
                    ) : (
                      <span className={classes.muted}>0</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className={classes.empty}>
                    No centres found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}

export default HqRollup
